import { Box, InputAdornment, useTheme } from '@mui/material';
import { IconSparkles } from '@tabler/icons-react';
import { openWidget } from 'actions/main';
import {
  handleFreeFormInput,
  setFreeFormQuery,
} from 'actions/workflows-v2/workflowsV2';
import IntentFormWrapper from 'components/FormWrapper/IntentFormWrapper';
import { OrgThemedButton } from 'components/OrgThemedButton';
import ProactiveBubbles from 'components/ProactiveBubbles';
import { TextInput } from 'components/TextInput/TextInput';
import { DEFAULT_MARGIN } from 'embed/constants';
import { useAppDispatch, useAppSelector } from 'hooks';
import React from 'react';
import {
  selectIsPrivacyConsented,
  selectLegibleElementColor,
  selectPlacement,
} from 'reducers/config';
import {
  selectBackToChatText,
  selectFreeFormInputPlaceholder,
  selectFreeFormQuery,
  selectIsConversationActive,
  selectIsFloatingBarFocused,
  selectIsProactiveChatVisible,
  setActionToExecAfterPrivacyConsented,
  setIsFloatingBarFocused,
  setShouldFocusTextInput,
} from 'reducers/workflows';
import { postIframeResizeEvent } from 'utils/postIframeResizeEvent';

export const FloatingBar = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const freeFormQuery = useAppSelector(selectFreeFormQuery);
  const placeholder = useAppSelector(selectFreeFormInputPlaceholder);
  const legibleElementColor = useAppSelector(selectLegibleElementColor);
  const isPrivacyConsented = useAppSelector(selectIsPrivacyConsented);
  const isFloatingBarFocused = useAppSelector(selectIsFloatingBarFocused);
  const isConversationActive = useAppSelector(selectIsConversationActive);
  const isProactiveChatVisible = useAppSelector(selectIsProactiveChatVisible);
  const backToChatText = useAppSelector(selectBackToChatText);
  const placement = useAppSelector(selectPlacement);

  React.useEffect(() => {
    /**
     * without this the iframe will resize its width after the floating bar appears
     */
    postIframeResizeEvent();
  }, [isFloatingBarFocused]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setFreeFormQuery(e.target.value));
  };

  const onSendMessage = (event: React.SyntheticEvent) => {
    event.preventDefault();

    dispatch(setShouldFocusTextInput(true));

    if (isConversationActive) {
      dispatch(openWidget());
      return;
    }

    if (!isPrivacyConsented) {
      dispatch(openWidget());
      dispatch(setActionToExecAfterPrivacyConsented('handleFreeFormInput'));
      return;
    }

    dispatch(openWidget());
    dispatch(handleFreeFormInput());
  };

  const EndpointAdornment = React.useMemo(() => {
    const onClick = () => {
      if (isConversationActive) {
        dispatch(openWidget());
      }
    };

    if (!isConversationActive) {
      return undefined;
    }

    return (
      <InputAdornment position='end'>
        <OrgThemedButton
          onClick={onClick}
          size='medium'
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.colors.slate[200],
            },
            backgroundColor: theme.palette.colors.slate[200],
            border: `1px solid ${theme.palette.colors.slate[300]}`,
            boxShadow: 'none',
            color: theme.palette.colors.black,
          }}
          variant='contained'
        >
          {backToChatText}
        </OrgThemedButton>
      </InputAdornment>
    );
  }, [dispatch, isConversationActive, theme, backToChatText]);

  // width of the floating bar
  const focusedWidth = `calc(100% - ${DEFAULT_MARGIN}px)`;
  const unfocusedWidth = `min(${focusedWidth}, 90%)`;
  const width = isFloatingBarFocused ? focusedWidth : unfocusedWidth;

  return (
    <Box
      onMouseEnter={() => {
        dispatch(setIsFloatingBarFocused(true));
      }}
      onMouseLeave={() => {
        dispatch(setIsFloatingBarFocused(false));
      }}
      sx={{
        display: 'flex',
        flexDirection: placement === 'top' ? 'column-reverse' : 'column',
        marginLeft: 'auto',
        marginRight: 'auto',
        transition:
          'width 0.2s cubic-bezier(0.4, 0, 0.6, 1), height 0.2s cubic-bezier(0.4, 0, 0.6, 1), transform 0.2s ease-in-out',
        width: width,
      }}
    >
      {/* intent options */}
      {isProactiveChatVisible && (
        <ProactiveBubbles key='proactive-bubbles' useResizeIds={false} />
      )}
      {/* floating bar input */}
      <IntentFormWrapper
        background={false}
        formTitle=''
        isDisabled={false}
        isForm
        isLastStep
        key='floating-bar-input'
        onSubmit={onSendMessage}
        stepIndex={-1}
      >
        <TextInput
          alwaysFocusedBorder
          aria-label={placeholder}
          disableSendMessage={!freeFormQuery.trim()}
          EndAdornment={EndpointAdornment}
          InputProps={{
            inputProps: {
              className: 'forethought-scrollbar',
              'data-testid': 'floating-bar-input',
              onBlur: () => {
                /**
                 * setTimeout ensures if the blur is from clicking send or clicking
                 * on an intent button that those still fire.
                 */
                setTimeout(() => {
                  dispatch(setIsFloatingBarFocused(false));
                }, 500);
              },
              onFocus: () => {
                dispatch(setIsFloatingBarFocused(true));
              },
            },
            startAdornment: (
              <InputAdornment position='start'>
                <IconSparkles color={legibleElementColor} size={24} />
              </InputAdornment>
            ),
            style: {
              backgroundColor: theme.palette.colors.white,
              boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.25)',
            },
          }}
          onChange={onChange}
          onSendMessage={onSendMessage}
          placeholder={placeholder}
          value={freeFormQuery}
          visuallyHiddenSendButton={!freeFormQuery.trim()}
        />
      </IntentFormWrapper>
    </Box>
  );
};
